import {
  AdjustmentAidFormValues,
  AdjustmentAidParticipant,
  ApplicationType,
  BankruptcyTexts,
  DismissedTexts,
  IllnessTexts,
} from '../../types/Application/adjustmentAid';
import { AdjustmentAidApplicationType } from '../../constants/Enum';
import { ApplicationFile, Worksite } from '../../types/Application/application';
import { validFileTypesAdjustmentAid } from '../../fileConfig';

interface AdjustmentAidState {
  form: AdjustmentAidFormValues;
  applicationType: ApplicationType | undefined;
  worksite: Worksite;
  fullTexts: DismissedTexts | IllnessTexts | BankruptcyTexts | undefined;
  terminatedParticipants: AdjustmentAidParticipant[];
  fileTerminatedParticipants?: ApplicationFile;
  extraInfo: string;
  moreApplications: boolean;
  union: { id: string; name: string };
  unionRole: boolean;
  myRole: boolean;
  isSameEmailForContacts: boolean;
  showErrorMessageExistingSSN: boolean;
  showErrorMessageMissingFileOrParticipants: boolean;
  hasExistingAutomatedBankruptcyIncident: boolean;
  existingAutomatedBankruptcyIncidentId?: string;
  isCheckingExistingAutomatedBankruptcyIncident: boolean;
  existingAutomatedBankruptcyIncidentLookupFailed: boolean;
  isExistingAutomatedBankruptcyIncidentFlowSuccess: boolean;
  isExistingAutomatedBankruptcyIncidentFlowSubmitting: boolean;
}

export const initialState: AdjustmentAidState = {
  form: {
    terminationDate: {
      name: 'terminationDate',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    orgNumber: {
      name: 'orgNumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ORGNUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    worksiteName: {
      name: 'worksiteName',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    worksiteAddress: {
      name: 'worksiteAddress',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    worksiteCity: {
      name: 'worksiteCity',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    worksiteZipCode: {
      name: 'worksiteZipCode',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ZIPCODE',
      isForeignAddress: false,
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    worksiteTelephone: {
      name: 'worksiteTelephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    unionContactFirstname: {
      name: 'unionContactFirstname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    unionContactLastname: {
      name: 'unionContactLastname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    unionContactAddress: {
      name: 'unionContactAddress',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    unionContactCity: {
      name: 'unionContactCity',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    unionContactZipCode: {
      name: 'unionContactZipCode',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ZIPCODE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    unionContactEmail: {
      name: 'unionContactEmail',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    unionContactConfirmEmail: {
      name: 'unionContactConfirmEmail',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      compareMail: '',
      autocomplete: 'off',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    unionContactTelephone: {
      name: 'unionContactTelephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    companyContactFirstname: {
      name: 'companyContactFirstname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    companyContactLastname: {
      name: 'companyContactLastname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    companyContactAddress: {
      name: 'companyContactAddress',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    companyContactCity: {
      name: 'companyContactCity',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    companyContactZipCode: {
      name: 'companyContactZipCode',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ZIPCODE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    companyContactEmail: {
      name: 'companyContactEmail',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    companyContactConfirmEmail: {
      name: 'companyContactConfirmEmail',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      autocomplete: 'off',
      compareMail: '',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    companyContactTelephone: {
      name: 'companyContactTelephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    amountDismissed: {
      name: 'amountDismissed',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'NUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
      ],
      validateOnSubmit: true,
    },
    personTelephone: {
      name: 'personTelephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personFirstname: {
      name: 'personFirstname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personLastname: {
      name: 'personLastname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personAddress: {
      name: 'personAddress',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personCity: {
      name: 'personCity',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personZipCode: {
      name: 'personZipCode',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ZIPCODE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personEmail: {
      name: 'personEmail',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personPersonalnumber: {
      name: 'personPersonalnumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PERSONALNUMBER',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
    },
    personEmployedFrom: {
      name: 'personEmployedFrom',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
      compareType: 'LESS_THAN',
    },
    personEmployedTo: {
      name: 'personEmployedTo',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
      compareType: 'GREATER_THAN',
    },
    personNoticeDate: {
      name: 'personNoticeDate',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: false,
      compareType: 'GREATER_THAN',
      compareTypeTwo: 'LESS_THAN',
    },
    templateFile: {
      name: 'templateFile',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'FILE',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validFileTypes: validFileTypesAdjustmentAid,
      validateOnSubmit: false,
    },
    unionDropdown: {
      name: 'unionDropdown',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    worksiteDropdown: {
      name: 'worksiteDropdown',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
    moreApplicationsRadioButtons: {
      name: 'moreApplicationsRadioButtons',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      applicationTypes: [
        AdjustmentAidApplicationType.Dismissed,
        AdjustmentAidApplicationType.Bankruptcy,
        AdjustmentAidApplicationType.Illness,
      ],
      validateOnSubmit: true,
    },
  },
  applicationType: undefined,
  worksite: {
    bisnodeId: '',
    worksiteName: '',
    orgNumber: '',
    address: '',
    city: '',
    postalCode: '',
    parentCompany: {
      bisnodeId: '',
      orgName: '',
      orgNumber: '',
      address: '',
      city: '',
      zipCode: '',
    },
  },
  fullTexts: undefined,
  terminatedParticipants: [],
  fileTerminatedParticipants: undefined,
  myRole: true,
  unionRole: true,
  extraInfo: '',
  union: { id: '', name: '' },
  moreApplications: true,
  isSameEmailForContacts: false,
  showErrorMessageExistingSSN: false,
  showErrorMessageMissingFileOrParticipants: false,
  hasExistingAutomatedBankruptcyIncident: false,
  existingAutomatedBankruptcyIncidentId: undefined,
  isCheckingExistingAutomatedBankruptcyIncident: false,
  existingAutomatedBankruptcyIncidentLookupFailed: false,
  isExistingAutomatedBankruptcyIncidentFlowSuccess: false,
  isExistingAutomatedBankruptcyIncidentFlowSubmitting: false,
};
